import { Plus, Trash2 } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { formatTL } from '../lib/pricing';

function parseMoneyInput(raw: string): number {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? 0 : parseInt(digits, 10);
}

export default function CostingSummary() {
  const {
    pricingCalculations, proposalType, units, activeUnitId, setActiveUnitId,
    setUnitManualPricing, readOnlyState,
    projectMeta, setProjectMeta, addUnit, removeUnit, renameUnit, setUnitQuantity,
    salesPointDiscount, setSalesPointDiscount,
  } = useAppContext();

  if (proposalType === 'bulk') {
    const grandTotal = units.reduce((sum, u) => sum + ((u.manualUnitPrice || 0) + (u.manualVat || 0)) * u.quantity, 0);
    return (
      <div className="bg-white p-6 rounded-xl border border-stone-200/80 shadow-card space-y-5">
        <div className="text-xs font-semibold text-stone-700 uppercase tracking-wider border-b border-stone-100 pb-3">
          Proje Geneli Özet
        </div>

        <div className="grid grid-cols-2 gap-3.5">
          <div>
            <label className="text-[11px] font-medium text-stone-500">Proje / Şantiye Adı</label>
            <input
              type="text"
              placeholder="Örn. Boz Yapı Projesi"
              disabled={readOnlyState.isReadOnly}
              value={projectMeta.projectName}
              onChange={e => setProjectMeta({ ...projectMeta, projectName: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-xs rounded-lg border border-stone-200/90 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed text-stone-800 transition-all"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-stone-500">Site Adı (opsiyonel)</label>
            <input
              type="text"
              placeholder="Örn. Suadiye Sitesi"
              disabled={readOnlyState.isReadOnly}
              value={projectMeta.siteName || ''}
              onChange={e => setProjectMeta({ ...projectMeta, siteName: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-xs rounded-lg border border-stone-200/90 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed text-stone-800 transition-all"
            />
          </div>
        </div>

        <div className="overflow-x-auto border border-stone-200/70 rounded-lg">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-stone-50/80 border-b border-stone-200/60 text-stone-500 text-[10px] uppercase font-semibold tracking-wider">
                <th className="text-left py-2.5 px-3">Daire Tipi</th>
                <th className="text-center py-2.5 px-2 w-16">Adet</th>
                <th className="text-right py-2.5 px-3 w-36">Özel İskontolu Birim Fiyat</th>
                <th className="text-right py-2.5 px-3 w-28">KDV</th>
                <th className="text-right py-2.5 px-3 w-28">Tutar</th>
                <th className="text-right py-2.5 px-3 w-32">Toplam Fiyat</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {units.map(unit => {
                const unitPrice = unit.manualUnitPrice || 0;
                const unitVat = unit.manualVat || 0;
                const unitAmount = unitPrice + unitVat;
                return (
                  <tr
                    key={unit.id}
                    onClick={() => setActiveUnitId(unit.id)}
                    className={`cursor-pointer transition-colors ${unit.id === activeUnitId ? 'bg-brand-50/40' : 'hover:bg-stone-50/60'}`}
                  >
                    <td className="py-2 px-3">
                      <input
                        type="text"
                        placeholder="İsimsiz Daire Tipi"
                        disabled={readOnlyState.isReadOnly}
                        value={unit.label}
                        onClick={e => e.stopPropagation()}
                        onChange={e => renameUnit(unit.id, e.target.value)}
                        className="w-full px-2 py-1 rounded border border-transparent hover:border-stone-200 focus:outline-none focus:border-brand-500 focus:bg-white bg-transparent font-medium text-stone-800"
                      />
                    </td>
                    <td className="py-2 px-2 text-center">
                      <input
                        type="number"
                        min={1}
                        disabled={readOnlyState.isReadOnly}
                        value={unit.quantity}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setUnitQuantity(unit.id, parseInt(e.target.value, 10) || 1)}
                        className="w-14 text-center px-1.5 py-1 rounded-md border border-stone-200 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed tabular-nums transition-all"
                      />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <input
                        type="text"
                        inputMode="numeric"
                        disabled={readOnlyState.isReadOnly}
                        value={unitPrice === 0 ? '' : unitPrice.toLocaleString('tr-TR')}
                        placeholder="0"
                        onClick={e => e.stopPropagation()}
                        onChange={e => setUnitManualPricing(unit.id, { manualUnitPrice: parseMoneyInput(e.target.value) })}
                        className="w-full text-right px-2 py-1 rounded-md border border-stone-200 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed tabular-nums transition-all"
                      />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <input
                        type="text"
                        inputMode="numeric"
                        disabled={readOnlyState.isReadOnly}
                        value={unitVat === 0 ? '' : unitVat.toLocaleString('tr-TR')}
                        placeholder="0"
                        onClick={e => e.stopPropagation()}
                        onChange={e => setUnitManualPricing(unit.id, { manualVat: parseMoneyInput(e.target.value) })}
                        className="w-full text-right px-2 py-1 rounded-md border border-stone-200 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed tabular-nums transition-all"
                      />
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-stone-600 font-medium">{formatTL(unitAmount)}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold text-stone-900">{formatTL(unitAmount * unit.quantity)}</td>
                    <td className="py-2 text-center">
                      {!readOnlyState.isReadOnly && units.length > 1 && (
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); removeUnit(unit.id); }}
                          className="text-stone-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Bu daire tipini kaldır"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!readOnlyState.isReadOnly && (
          <button
            type="button"
            onClick={addUnit}
            className="w-full py-2 bg-brand-50/80 hover:bg-brand-100 text-brand-700 border border-brand-200/60 rounded-lg text-xs font-semibold flex items-center justify-center space-x-1.5 transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Daire Tipi Ekle</span>
          </button>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
          <span className="text-[11px] font-semibold text-stone-500 uppercase tracking-wide">
            Toplam Daire: {units.reduce((sum, u) => sum + u.quantity, 0)}
          </span>
          <div className="text-right">
            <span className="text-[10px] uppercase font-semibold text-stone-400 block tracking-wider">Genel Proje Toplamı (KDV Dahil)</span>
            <span className="text-xl font-bold text-stone-900">{formatTL(grandTotal)}</span>
          </div>
        </div>
      </div>
    );
  }

  const discountedTotal = Math.max(0, pricingCalculations.finalTotal - salesPointDiscount);

  return (
    <div className="bg-[#18181B] text-white p-6 rounded-xl border border-stone-800/80 shadow-card space-y-5">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="grid grid-cols-3 gap-6 text-center md:text-left w-full md:w-auto">
          <div>
            <span className="text-[10.5px] uppercase font-medium text-stone-400 block tracking-wider">Tavsiye Liste Toplamı</span>
            <span className="text-sm font-semibold line-through text-stone-500">
              {formatTL(pricingCalculations.listGrandTotal)}
            </span>
          </div>
          <div>
            <span className="text-[10.5px] uppercase font-medium text-stone-400 block tracking-wider">Kampanya İndirimi</span>
            <span className="text-sm font-semibold text-emerald-400">
              -{formatTL(pricingCalculations.campaignSavings)}
            </span>
          </div>
          <div>
            <span className="text-[10.5px] uppercase font-medium text-stone-400 block tracking-wider">KDV Toplamı</span>
            <span className="text-sm font-semibold text-stone-300">
              {formatTL(pricingCalculations.totalVat)}
            </span>
          </div>
        </div>

        <div className="bg-[#945e46] border border-brand-400/30 px-6 py-3.5 rounded-xl text-center md:text-right w-full md:w-auto shadow-sm">
          <span className="text-[10px] uppercase font-semibold text-brand-100 block tracking-wider">
            GENEL TOPLAM (KDV Dahil)
          </span>
          <span className="text-2xl font-bold tracking-tight text-white block mt-0.5">
            {formatTL(discountedTotal)}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 pt-4 border-t border-stone-800/80">
        <div>
          <label className="text-[11px] font-medium text-stone-400">Satış Noktası Özel İskonto (KDV Dahil)</label>
          <p className="text-[10px] text-stone-500 mt-0.5">Küsuratlı çıkan teklifleri yuvarlamak için; Genel Toplam'dan doğrudan düşülür.</p>
        </div>
        <input
          type="text"
          inputMode="numeric"
          disabled={readOnlyState.isReadOnly}
          value={salesPointDiscount === 0 ? '' : salesPointDiscount.toLocaleString('tr-TR')}
          placeholder="0"
          onChange={e => setSalesPointDiscount(parseMoneyInput(e.target.value))}
          className="w-36 shrink-0 text-right px-3 py-2 text-sm rounded-lg border border-stone-700 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-stone-900 text-white placeholder:text-stone-600 disabled:bg-stone-800 disabled:cursor-not-allowed tabular-nums transition-all"
        />
      </div>
    </div>
  );
}
